import { Upload } from 'lucide-react';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Button } from './Button';
import styles from './DropZone.module.css';

// R4 (IMP-01 "Drop zone"): drag a file onto it, or press the button, which opens the browser's
// chooser behind our own Button — never the stock file input (CLAUDE.md §5). Design card:
// `Design System/project/guidelines/drop-zone.card.html`.

interface DropZoneProps {
  /** Names the chooser for a screen reader ("Choose the applicants' file"). */
  readonly label: string;
  readonly title: string;
  readonly hint: string;
  readonly accept: string;
  readonly isPending: boolean;
  readonly pendingText?: string;
  /** The §L26 message for the last file refused, shown under the box. */
  readonly error?: string | null;
  /** Several files at once (ADR 0046's CV Bank upload): `onFile` is called for each. */
  readonly isMultiple?: boolean;
  readonly onFile: (file: File) => void;
}

export function DropZone({ label, title, hint, accept, isPending, pendingText = 'Reading…', error = null, isMultiple = false, onFile }: DropZoneProps): ReactNode {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOver, setIsOver] = useState(false);

  const chooseText = isMultiple ? 'Choose files' : 'Choose a file';

  const take = (files: readonly File[]): void => {
    setIsOver(false);
    if (isPending) return;
    for (const file of files.slice(0, isMultiple ? undefined : 1)) onFile(file);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsOver(true);
  };

  return (
    <div className={styles.wrap}>
      <div
        className={styles.zone}
        data-over={isOver}
        onDragOver={onDragOver}
        onDragLeave={() => setIsOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          take(Array.from(event.dataTransfer.files));
        }}
      >
        <span className={styles.icon} aria-hidden>
          <Upload size={16} />
        </span>
        <span className={styles.title}>{isOver ? 'Drop to upload' : title}</span>
        <input
          ref={inputRef}
          type="file"
          className={styles.hiddenFile}
          accept={accept}
          multiple={isMultiple}
          aria-label={label}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            take(files);
          }}
        />
        <Button variant="outline" size="sm" isPending={isPending} onClick={() => inputRef.current?.click()}>
          {isPending ? pendingText : chooseText}
        </Button>
        <span className={styles.hint}>{hint}</span>
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
